import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import {
  Check, Printer, CreditCard, Banknote, Receipt, Percent, Tag,
  CloudOff, WifiOff, Cloud, RefreshCw, Award, Phone, X, Search,
  Clock, PauseCircle, PlayCircle
} from 'lucide-react';

const QUICK_CASH = [5, 10, 20, 50];

export const PaymentModal = ({ isOpen, onClose, subtotal, vatAmount, total, discount, onConfirm, settings, canUseAccount }) => {
  const [paymentMethod, setPaymentMethod] = useState(settings?.default_method || 'card');
  const [cashTendered, setCashTendered] = useState('');
  const [accountSignatory, setAccountSignatory] = useState('');
  const [printReceipt, setPrintReceipt] = useState(true);

  const cashChange = cashTendered ? parseFloat(cashTendered) - total : 0;

  const handleConfirm = () => {
    if (paymentMethod === 'cash' && (!cashTendered || parseFloat(cashTendered) < total)) {
      toast.error('Insufficient cash amount'); return;
    }
    if (paymentMethod === 'account' && !accountSignatory.trim()) {
      toast.error('Signatory name required for account payment'); return;
    }
    onConfirm({
      paymentMethod,
      cashTendered: paymentMethod === 'cash' ? parseFloat(cashTendered) : null,
      cashChange: paymentMethod === 'cash' ? cashChange : null,
      accountSignatory: paymentMethod === 'account' ? accountSignatory : null,
      printReceipt
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Payment</DialogTitle>
          <DialogDescription className="sr-only">Select payment method</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm"><span>Subtotal</span><span>£{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-sm"><span>VAT</span><span>£{vatAmount.toFixed(2)}</span></div>
            {discount > 0 && (
              <div className="flex justify-between text-sm text-green-600"><span>Discount</span><span>-£{discount.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between font-bold text-lg pt-2 border-t"><span>Total</span><span>£{total.toFixed(2)}</span></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {settings?.card_enabled !== false && (
              <Button variant={paymentMethod === 'card' ? 'default' : 'outline'} className="h-16 flex-col"
                onClick={() => setPaymentMethod('card')} data-testid="payment-card">
                <CreditCard className="w-6 h-6 mb-1" /><span className="text-xs">Card</span>
              </Button>
            )}
            {settings?.cash_enabled !== false && (
              <Button variant={paymentMethod === 'cash' ? 'default' : 'outline'} className="h-16 flex-col"
                onClick={() => setPaymentMethod('cash')} data-testid="payment-cash">
                <Banknote className="w-6 h-6 mb-1" /><span className="text-xs">Cash</span>
              </Button>
            )}
            {settings?.account_enabled !== false && canUseAccount && (
              <Button variant={paymentMethod === 'account' ? 'default' : 'outline'} className="h-16 flex-col"
                onClick={() => setPaymentMethod('account')} data-testid="payment-account">
                <Receipt className="w-6 h-6 mb-1" /><span className="text-xs">Account</span>
              </Button>
            )}
          </div>
          {paymentMethod === 'cash' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Amount Tendered</Label>
                <Input type="number" step="0.01" value={cashTendered} onChange={(e) => setCashTendered(e.target.value)}
                  placeholder="0.00" className="text-2xl h-14 text-center" data-testid="cash-tendered-input" />
              </div>
              <div className="grid grid-cols-4 gap-2">
                {QUICK_CASH.map((amount) => (
                  <Button key={amount} variant="outline" onClick={() => setCashTendered(amount.toString())} className="h-12">£{amount}</Button>
                ))}
              </div>
              {cashTendered && parseFloat(cashTendered) >= total && (
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-green-600">Change Due</p>
                  <p className="price-display text-3xl text-green-700">£{cashChange.toFixed(2)}</p>
                </div>
              )}
            </div>
          )}
          {paymentMethod === 'account' && (
            <div className="space-y-2">
              <Label>Signatory Name (Required)</Label>
              <Input value={accountSignatory} onChange={(e) => setAccountSignatory(e.target.value)}
                placeholder="Enter name of person signing" data-testid="account-signatory-input" />
              <p className="text-xs text-slate-500">The signatory must sign the printed receipt</p>
            </div>
          )}
          {settings?.require_receipt_confirmation && (
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <Label>Print Receipt</Label>
              <Button variant={printReceipt ? 'default' : 'outline'} size="sm" onClick={() => setPrintReceipt(!printReceipt)}>
                <Printer className="w-4 h-4 mr-1" />{printReceipt ? 'Yes' : 'No'}
              </Button>
            </div>
          )}
          <Button onClick={handleConfirm} className="w-full h-14 text-lg" data-testid="confirm-payment-btn">Confirm Payment</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SaleConfirmation = ({ sale, onClose, onPrint }) => {
  const isOffline = sale?.offline;
  return (
    <Dialog open={!!sale} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="sale-confirmation-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-center">
            {isOffline ? <CloudOff className="w-12 h-12 mx-auto mb-2 text-amber-500" /> : <Check className="w-12 h-12 mx-auto mb-2 text-green-500" />}
            {isOffline ? 'Sale Queued' : 'Sale Complete'}
          </DialogTitle>
          <DialogDescription className="sr-only">{isOffline ? 'Sale queued for sync' : 'Transaction completed'}</DialogDescription>
        </DialogHeader>
        {sale && (
          <div className="space-y-4">
            {isOffline && (
              <div className="bg-amber-50 text-amber-700 p-3 rounded-lg text-sm flex items-center gap-2">
                <WifiOff className="w-4 h-4 flex-shrink-0" />
                <span>This sale is queued offline and will sync when connected</span>
              </div>
            )}
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-sm text-slate-500">Order Number</p>
              <p className="font-heading text-4xl font-bold text-slate-900">#{sale.order_number || 'OFFLINE'}</p>
            </div>
            <div className="border rounded-lg p-4">
              <div className="space-y-2">
                {sale.items.slice(0, 5).map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.quantity}x {item.product_name}</span>
                    <span>£{(item.quantity * (item.unit_price_inc_vat || item.unit_price)).toFixed(2)}</span>
                  </div>
                ))}
                {sale.items.length > 5 && <div className="text-sm text-slate-400">+{sale.items.length - 5} more items</div>}
              </div>
              <div className="border-t mt-3 pt-3">
                <div className="flex justify-between font-bold text-lg"><span>Total</span><span>£{sale.total.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm text-slate-500 mt-1"><span>Payment</span><span className="capitalize">{sale.payment_method}</span></div>
              </div>
            </div>
            <div className="flex gap-3">
              {!isOffline && (
                <Button variant="outline" data-testid="print-receipt-btn" onClick={() => onPrint(sale.id)} className="flex-1 h-14 text-lg touch-press">
                  <Printer className="w-5 h-5 mr-2" /> Print Receipt
                </Button>
              )}
              <Button data-testid="new-sale-btn" onClick={onClose} className={`h-14 text-lg touch-press ${isOffline ? 'w-full' : 'flex-1'}`}>New Sale</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const DiscountModal = ({ isOpen, onClose, onApply }) => {
  const [type, setType] = useState('percentage');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');

  const handleApply = () => {
    if (!value || parseFloat(value) <= 0) { toast.error('Enter a valid discount amount'); return; }
    onApply({ type, value: parseFloat(value), reason });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="discount-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Apply Discount</DialogTitle>
          <DialogDescription className="sr-only">Add a discount to this order</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <Button variant={type === 'percentage' ? 'default' : 'outline'} onClick={() => setType('percentage')} className="h-12">
              <Percent className="w-4 h-4 mr-2" /> Percentage
            </Button>
            <Button variant={type === 'fixed' ? 'default' : 'outline'} onClick={() => setType('fixed')} className="h-12">£ Fixed</Button>
          </div>
          <div className="space-y-2">
            <Label>Amount {type === 'percentage' ? '(%)' : '(£)'}</Label>
            <Input type="number" step={type === 'percentage' ? '1' : '0.01'} value={value}
              onChange={(e) => setValue(e.target.value)} placeholder={type === 'percentage' ? 'e.g., 10' : 'e.g., 2.50'}
              className="text-xl h-12" data-testid="discount-value-input" />
          </div>
          <div className="space-y-2">
            <Label>Reason (optional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g., Staff discount" />
          </div>
          <Button onClick={handleApply} className="w-full h-12" data-testid="apply-discount-btn">Apply Discount</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const OfflineQueueDialog = ({ isOpen, onClose, isOnline, pendingSales, isSyncing, onSync }) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-lg" data-testid="offline-queue-modal">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl flex items-center gap-2">
          <CloudOff className="w-5 h-5" /> Offline Sales Queue
        </DialogTitle>
        <DialogDescription className="sr-only">Manage offline sales</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className={`flex items-center gap-2 p-3 rounded-lg ${isOnline ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
          {isOnline ? <Cloud className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
          <span>{isOnline ? 'Connected - sales will sync automatically' : 'Offline - sales will be queued locally'}</span>
        </div>
        {pendingSales.length === 0 ? (
          <p className="text-center py-8 text-slate-400">No pending sales</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {pendingSales.map((sale) => (
              <div key={sale.localId} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">£{sale.total.toFixed(2)}</p>
                  <p className="text-xs text-slate-500">{sale.items.length} items</p>
                </div>
                <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700">{sale.status}</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-3">
          {pendingSales.length > 0 && isOnline && (
            <Button onClick={onSync} disabled={isSyncing} className="flex-1">
              <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} /> Sync All
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className={pendingSales.length > 0 && isOnline ? '' : 'w-full'}>Close</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const CustomerSearchDialog = ({ isOpen, onClose, customerPhone, setCustomerPhone, onSearch }) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-sm" data-testid="customer-search-modal">
      <DialogHeader>
        <DialogTitle>Link Customer</DialogTitle>
        <DialogDescription>Search by phone number</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 mt-2">
        <div className="flex gap-2">
          <Input data-testid="customer-phone-input" placeholder="07700 900 123" value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onSearch()} autoFocus />
          <Button onClick={onSearch} data-testid="search-customer-btn"><Search className="w-4 h-4" /></Button>
        </div>
        <p className="text-xs text-slate-400">Enter customer phone. If not found, you can register them.</p>
      </div>
    </DialogContent>
  </Dialog>
);

export const NewCustomerDialog = ({ isOpen, onClose, form, setForm, onSubmit }) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-sm" data-testid="new-customer-modal">
      <DialogHeader>
        <DialogTitle>Register New Customer</DialogTitle>
        <DialogDescription>Phone not found — create a new loyalty account</DialogDescription>
      </DialogHeader>
      <div className="space-y-3 mt-2">
        <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm(f => ({...f, phone: e.target.value}))} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>First Name</Label><Input data-testid="new-cust-till-first" value={form.first_name} onChange={(e) => setForm(f => ({...f, first_name: e.target.value}))} /></div>
          <div><Label>Surname</Label><Input data-testid="new-cust-till-surname" value={form.surname} onChange={(e) => setForm(f => ({...f, surname: e.target.value}))} /></div>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
          <input type="checkbox" checked={form.gdpr_consent} onChange={(e) => setForm(f => ({...f, gdpr_consent: e.target.checked}))} className="rounded" />
          GDPR consent for loyalty data storage
        </label>
        <Button className="w-full" data-testid="register-customer-btn" onClick={onSubmit}>Register & Link</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const RedemptionDialog = ({ isOpen, onClose, linkedCustomer, loyaltySettings, onRedeem }) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-sm" data-testid="redemption-modal">
      <DialogHeader>
        <DialogTitle>Redeem Points</DialogTitle>
        <DialogDescription>{linkedCustomer?.first_name} has {linkedCustomer?.points_balance} points</DialogDescription>
      </DialogHeader>
      <div className="space-y-3 mt-2">
        {linkedCustomer?.points_balance >= (loyaltySettings?.redemption_rate || 100) && (
          <button data-testid="redeem-points-option"
            onClick={() => {
              const rate = loyaltySettings?.redemption_rate || 100;
              const usable = Math.floor(linkedCustomer.points_balance / rate) * rate;
              const disc = (usable / rate).toFixed(2);
              onRedeem({ type: 'points', points: usable });
              onClose();
              toast.success(`£${disc} loyalty discount applied`);
            }}
            className="w-full p-4 text-left rounded-lg border-2 border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors">
            <div className="flex items-center justify-between">
              <div><p className="font-medium text-slate-800">Points Discount</p><p className="text-sm text-slate-500">{loyaltySettings?.redemption_rate || 100} pts = £1 off</p></div>
              <div className="text-right">
                <p className="text-lg font-bold text-blue-600">£{(Math.floor(linkedCustomer.points_balance / (loyaltySettings?.redemption_rate || 100))).toFixed(2)} off</p>
                <p className="text-xs text-slate-400">using {Math.floor(linkedCustomer.points_balance / (loyaltySettings?.redemption_rate || 100)) * (loyaltySettings?.redemption_rate || 100)} pts</p>
              </div>
            </div>
          </button>
        )}
        {linkedCustomer?.points_balance >= (loyaltySettings?.meal_deal_points || 500) && (
          <button data-testid="redeem-meal-deal-option"
            onClick={() => {
              onRedeem({ type: 'meal_deal', points: loyaltySettings?.meal_deal_points || 500 });
              onClose();
              toast.success(`Meal deal redeemed (£${(loyaltySettings?.meal_deal_value || 3.50).toFixed(2)} off)`);
            }}
            className="w-full p-4 text-left rounded-lg border-2 border-slate-200 hover:border-green-400 hover:bg-green-50 transition-colors">
            <div className="flex items-center justify-between">
              <div><p className="font-medium text-slate-800">Free Meal Deal</p><p className="text-sm text-slate-500">{loyaltySettings?.meal_deal_points || 500} pts = £{(loyaltySettings?.meal_deal_value || 3.50).toFixed(2)} meal</p></div>
              <div className="text-right"><p className="text-lg font-bold text-green-600">£{(loyaltySettings?.meal_deal_value || 3.50).toFixed(2)} off</p></div>
            </div>
          </button>
        )}
        <Button variant="outline" className="w-full" onClick={onClose}>Cancel</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const DeferredOrdersDialog = ({ isOpen, onClose, orders, onRecall, onCancel }) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-amber-600" /> Parked Orders</DialogTitle>
        <DialogDescription>Orders waiting for payment</DialogDescription>
      </DialogHeader>
      <div className="space-y-3 mt-2">
        {orders.length === 0 ? (
          <p className="text-center text-slate-400 py-6">No parked orders</p>
        ) : (
          orders.map(order => (
            <div key={order.id} data-testid={`deferred-${order.id}`} className="p-3 rounded-lg border border-slate-200 bg-slate-50 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-800 text-sm">{order.customer_name || 'Walk-in'} &middot; £{order.subtotal.toFixed(2)}</p>
                  <p className="text-xs text-slate-500">{new Date(order.created_at).toLocaleTimeString()} · {order.operator_name}</p>
                </div>
              </div>
              <div className="text-xs text-slate-600">
                {order.items.map((item, i) => (<span key={i}>{i > 0 && ', '}{item.product_name} x{item.quantity}</span>))}
              </div>
              {order.note && <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">{order.note}</p>}
              <div className="flex gap-2">
                <Button size="sm" data-testid={`recall-${order.id}`} className="flex-1" onClick={() => onRecall(order)}>
                  <PlayCircle className="w-3 h-3 mr-1" /> Recall
                </Button>
                <Button size="sm" variant="outline" className="text-red-500" data-testid={`cancel-deferred-${order.id}`} onClick={() => onCancel(order.id)}>
                  <X className="w-3 h-3 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </DialogContent>
  </Dialog>
);
